import { z } from "zod";
import { Finite, IsoDate, Symbol_ } from "./primitives";

/** One daily OHLCV bar, split- and dividend-adjusted where the provider supplies it. */
export const OhlcvBar = z.object({
  date: IsoDate,
  open: Finite.positive(),
  high: Finite.positive(),
  low: Finite.positive(),
  close: Finite.positive(),
  /** Adjusted close. Always use this for returns; `close` is for display only. */
  adjClose: Finite.positive(),
  volume: Finite.nonnegative(),
});
export type OhlcvBar = z.infer<typeof OhlcvBar>;

/** A price history for one symbol, ascending by date. */
export const PriceSeries = z.object({
  symbol: Symbol_,
  bars: z.array(OhlcvBar),
});
export type PriceSeries = z.infer<typeof PriceSeries>;

/**
 * GICS-style sector. Scoring is z-scored *within* sector, so this is required
 * for a symbol to be scoreable at all; unclassified names fall back to
 * "Unknown" and are pooled together rather than silently compared against
 * unrelated businesses.
 */
export const Sector = z.enum([
  "Energy",
  "Materials",
  "Industrials",
  "ConsumerDiscretionary",
  "ConsumerStaples",
  "HealthCare",
  "Financials",
  "InformationTechnology",
  "CommunicationServices",
  "Utilities",
  "RealEstate",
  "Unknown",
]);
export type Sector = z.infer<typeof Sector>;

export const SecurityMeta = z.object({
  symbol: Symbol_,
  name: z.string(),
  sector: Sector,
  industry: z.string().nullable(),
  exchange: z.string().nullable(),
  /**
   * True for holdings with no public market data (e.g. pre-IPO SpaceX). These
   * are carried through the portfolio view as unpriced positions and are
   * excluded from scoring entirely — there is no honest way to score them.
   */
  unpriced: z.boolean().default(false),
});
export type SecurityMeta = z.infer<typeof SecurityMeta>;

/**
 * Fundamental data for one symbol.
 *
 * Stores valuation and quality *ratios* rather than raw financial-statement
 * components. That is not a shortcut: Finnhub's free tier does not expose raw
 * EBITDA or free cash flow at all, but it does publish the derived ratios
 * directly. Reconstructing EV/EBITDA from components that are always null
 * yields a value sleeve that is blind for every name while looking like it
 * works.
 *
 * `asOf` is the date the figures were *reported*, not the date we fetched
 * them. The backtest lags on this field to avoid using numbers that were not
 * yet public at the simulated decision point.
 */
export const Fundamentals = z.object({
  symbol: Symbol_,
  asOf: IsoDate,

  marketCap: Finite.nullable(),
  enterpriseValue: Finite.nullable(),
  sharesOutstanding: Finite.nullable(),

  // Valuation. Lower is cheaper for each of these.
  evToEbitda: Finite.nullable(),
  evToRevenue: Finite.nullable(),
  priceToFcf: Finite.nullable(),
  priceToSales: Finite.nullable(),
  priceToEarnings: Finite.nullable(),

  // Quality. Higher is better except debtToEquity.
  returnOnEquity: Finite.nullable(),
  grossMargin: Finite.nullable(),
  operatingMargin: Finite.nullable(),
  netProfitMargin: Finite.nullable(),
  currentRatio: Finite.nullable(),
  debtToEquity: Finite.nullable(),

  // Growth.
  revenueGrowthYoy: Finite.nullable(),
  epsGrowthYoy: Finite.nullable(),
});
export type Fundamentals = z.infer<typeof Fundamentals>;

/** Fields the value and quality sleeves read. Coverage is checked at ingest. */
export const FUNDAMENTAL_FACTOR_FIELDS = [
  "evToEbitda",
  "evToRevenue",
  "priceToFcf",
  "priceToSales",
  "priceToEarnings",
  "returnOnEquity",
  "grossMargin",
  "operatingMargin",
  "netProfitMargin",
  "currentRatio",
  "debtToEquity",
  "revenueGrowthYoy",
  "epsGrowthYoy",
] as const satisfies readonly (keyof Fundamentals)[];

/** Next scheduled earnings date, used for the earnings-proximity risk flag. */
export const EarningsDate = z.object({
  symbol: Symbol_,
  date: IsoDate,
  confirmed: z.boolean(),
});
export type EarningsDate = z.infer<typeof EarningsDate>;
